import { colors, measure } from "@obs/tokens/index.stylex.ts";
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { Fragment } from "react";

import type { Semantic } from "../../../../utils/semantic";
import { Tag } from "../../../Tag";
import { Typography } from "../../../Typography";

export type ContentProps = DynamicContentProps<ContentTitleProps>;

export type DynamicContentProps<TitleProps extends ContentTitleProps> = {
	disabled?: boolean;
	selected?: boolean;
} & (
	| {
			type: "DEFAULT";
			title: TitleProps & { semantic?: Semantic };
			description?: string;
	  }
	| {
			type: "SECONDARY";
			title: TitleProps;
	  }
	| {
			type: "TAG_TITLE";
			tag: ContentTagProps;
			description?: string;
	  }
	| {
			type: "TAG_META";
			title: TitleProps & { semantic?: Semantic };
			tag: ContentTagProps;
	  }
	| {
			type: "UNSAFE";
			Component: ReactNode;
			description?: string;
	  }
);

const styles = stylex.create({
	root: {
		display: "flex",
		flexDirection: "column",
		flexGrow: 1,
		overflow: "hidden",
		rowGap: measure.spacingGapMin,
		justifyContent: "center",
		alignItems: "flex-start",
	},
	title: {
		gap: measure.spacingGapMin,
		alignItems: "center",
	},
	titleSelected: {
		color: colors.neutralTxtInverted,
	},
});

const titleSemanticStyles = stylex.create({
	default: { color: colors.neutralTxtDefault },
	accent: { color: colors.accentTxtDefault },
	secondary: { color: colors.neutralTxtModerate },
	disabled: { color: colors.disabledTxtDefault },
	success: { color: colors.successTxtDefault },
	warning: { color: colors.warningTxtDefault },
	danger: { color: colors.dangerTxtDefault },
});

export const Content = ({ disabled, selected, ...props }: ContentProps) => {
	let content: ReactNode;
	switch (props.type) {
		case "TAG_TITLE":
			content = (
				<Fragment>
					<ContentTag {...props.tag} />
					{props.description && (
						<ContentDescription content={props.description} disabled={disabled} selected={selected} />
					)}
				</Fragment>
			);
			break;
		case "TAG_META":
			content = (
				<Fragment>
					<ContentTitle {...props.title} semantic={disabled ? "disabled" : props.title.semantic} selected={selected} />
					<ContentTag {...props.tag} />
				</Fragment>
			);
			break;
		case "SECONDARY":
			content = (
				<ContentTitle
					{...props.title}
					variant="body-default"
					semantic={disabled ? "disabled" : "secondary"}
					selected={selected}
				/>
			);
			break;
		case "UNSAFE":
			content = (
				<Fragment>
					{props.Component}
					{props.description && (
						<ContentDescription content={props.description} disabled={disabled} selected={selected} />
					)}
				</Fragment>
			);
			break;
		case "DEFAULT":
			content = (
				<Fragment>
					<ContentTitle {...props.title} semantic={disabled ? "disabled" : props.title.semantic} selected={selected} />
					{props.description && (
						<ContentDescription content={props.description} disabled={disabled} selected={selected} />
					)}
				</Fragment>
			);
			break;
	}
	return <span {...stylex.props(styles.root)}>{content}</span>;
};

type ContentTitleProps = {
	content: string;
	selected?: boolean;
};

const ContentTitle = ({
	semantic,
	content,
	selected,
	variant = "label-default",
}: ContentTitleProps & { semantic?: Semantic; variant?: "label-default" | "body-default" }) => {
	const colorStyle = selected ? styles.titleSelected : titleSemanticStyles[semantic ?? "default"];

	if (variant === "body-default") {
		return (
			<Typography.BodyDefault render={<span />} sx={[styles.title, colorStyle]}>
				{content}
			</Typography.BodyDefault>
		);
	}

	return <Typography.LabelDefault sx={[styles.title, colorStyle]}>{content}</Typography.LabelDefault>;
};

export type ContentTagProps = Omit<Tag.TagProps, "color" | "backgroundColor"> & {
	variant: "accent" | "accentWeak" | "danger" | "neutralStrong" | "success" | "warning";
};

const ContentTag = ({ variant, ...props }: ContentTagProps) => {
	switch (variant) {
		case "accent":
			return <Tag.Accent {...props} />;
		case "accentWeak":
			return <Tag.AccentWeak {...props} />;
		case "danger":
			return <Tag.Danger {...props} />;
		case "neutralStrong":
			return <Tag.NeutralStrong {...props} />;
		case "success":
			return <Tag.Success {...props} />;
		case "warning":
			return <Tag.Warning {...props} />;
	}
};

type ContentDescriptionProps = { disabled?: boolean; selected?: boolean; content?: string };

const descriptionStyles = stylex.create({
	default: {
		color: colors.neutralTxtModerate,
	},
	disabled: {
		color: colors.disabledTxtDefault,
	},
	selected: {
		color: colors.neutralTxtInverted,
	},
});

const ContentDescription = ({ content, disabled, selected }: ContentDescriptionProps) => {
	return (
		<Typography.BodyDefault
			render={<span />}
			sx={[disabled ? descriptionStyles.disabled : descriptionStyles.default, selected && descriptionStyles.selected]}
		>
			{content}
		</Typography.BodyDefault>
	);
};
